import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Crown, Sprout } from "lucide-react";

/**
 * Item 4 (2026-07): "My community" — merges the old "Collectives I lead" card
 * and the hosted-events half of "Your community events" into one card with
 * two short lists. Hour-approval actions moved to the My activity tabs.
 * Hidden entirely when the user neither leads a collective nor hosts events.
 */
export function MyCommunityCard({ hosted }) {
  const [led, setLed] = useState([]);
  useEffect(() => {
    api.myCollectives()
      .then((d) => setLed((d.collectives || []).filter((c) => c.is_leader)))
      .catch(() => {});
  }, []);

  if (!led.length && !(hosted || []).length) return null;

  return (
    <div data-testid="my-community-card" className="mt-10 bg-white border border-neutral-200 rounded-xl p-6">
      <h2 className="font-display text-2xl text-forest tracking-tight">My community</h2>
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold flex items-center gap-1.5">
            <Crown className="w-3.5 h-3.5 text-terracotta" /> Collectives I lead
          </div>
          <div className="mt-3 space-y-2">
            {led.length === 0 && (
              <div className="text-sm text-neutral-500">
                None yet — <Link to="/collectives" className="text-forest underline">find or start one</Link>.
              </div>
            )}
            {led.map((c) => (
              <Link
                key={c.id}
                to={`/collectives/${c.id}`}
                data-testid={`led-collective-${c.id}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-neutral-100 hover:border-forest/30 px-4 py-3 transition-colors"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-forest truncate">{c.name}</div>
                  <div className="text-xs text-neutral-500">{c.member_count} member{c.member_count === 1 ? "" : "s"}</div>
                </div>
                {(c.pending_member_ids?.length || 0) > 0 && (
                  <Badge data-testid={`led-collective-pending-${c.id}`} className="rounded-full bg-terracotta text-white border-none text-[11px] shrink-0">
                    {c.pending_member_ids.length} pending
                  </Badge>
                )}
              </Link>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold flex items-center gap-1.5">
            <Sprout className="w-3.5 h-3.5 text-terracotta" /> Your community events
          </div>
          <div className="mt-3 space-y-2" data-testid="hosted-events-list">
            {(hosted || []).length === 0 && (
              <div className="text-sm text-neutral-500">
                Nothing hosted yet — <Link to="/post-opportunity" className="text-forest underline">host an event</Link>.
              </div>
            )}
            {(hosted || []).map((o) => (
              <Link key={o.id} to={`/opportunities/${o.id}`} className="block rounded-xl border border-neutral-100 hover:border-forest/30 px-4 py-3 transition-colors">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="text-sm font-medium text-forest truncate">{o.title}</div>
                  <Badge className="bg-terracotta/10 text-terracotta border-none rounded-full text-[11px] shrink-0">{o.applicant_count || 0} joined</Badge>
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  {o.location}
                  {o.event_date ? ` · ${new Date(o.event_date).toLocaleDateString()}` : (o.commitment_type === "ongoing" ? " · Ongoing" : "")}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
